import React, { useState } from 'react'
import PropTypes from 'prop-types'
import AlertIcon from '@boltui/icons/src/components/AlertIcon'
import InfoIcon from '@boltui/icons/src/components/InfoIcon'
import AvailableIcon from '@boltui/icons/src/components/AvailableIcon'
import CloseIcon from '@boltui/icons/src/components/CloseIcon'
import StyledAlert from './Alert.styles'

const Alert = ({
  children,
  kind,
  isCloseable,
  closeButtonText,
  closeButtonAriaLabel,
  onClose,
  role,
  closeIcon,
  className,
  'data-test': dataTest,
  CustomIcon,
  ...props
}) => {
  const [show, toggleShow] = useState(true)
  if (!show) return null
  return (
    <StyledAlert
      kind={kind}
      role={role}
      className={className}
      isCloseable={isCloseable}
      data-test={dataTest}
      {...props}>
      {kind === 'success' && !CustomIcon && <AvailableIcon aria-hidden='true' />}
      {kind === 'info' && !CustomIcon && <InfoIcon aria-hidden='true' />}
      {kind === 'error' && !CustomIcon && <AlertIcon aria-hidden='true' />}
      {CustomIcon && <CustomIcon aria-hidden='true' />}
      {children}
      {isCloseable && (
        <button
          aria-label={closeButtonAriaLabel}
          data-test={`${dataTest}-button-close`}
          onClick={() => {
            toggleShow(false)
            onClose()
          }}>
          {closeIcon ? <CloseIcon aria-hidden='true' /> : closeButtonText}
        </button>
      )}
    </StyledAlert>
  )
}

Alert.propTypes = {
  /**
   * Optional prop to specify the kind of the Alert
   */
  kind: PropTypes.oneOf(['success', 'info', 'error']).isRequired,

  /**
   * Optional prop to allow the alert to be dismissed/closed
   */
  isCloseable: PropTypes.bool,

  /**
   * Optional prop to change the text shown for the close button
   */
  closeButtonText: PropTypes.string,

  /**
   * Optional prop to change the aria-label for the close button
   */
  closeButtonAriaLabel: PropTypes.string,

  /**
   * Optional prop for an onClose callback func
   */
  onClose: PropTypes.func,

  /**
   * Optional prop to specify the role of the Alert
   */
  role: PropTypes.string,

  /**
   * Optional prop to customize the icon for the Alert
   */
  CustomIcon: PropTypes.elementType,

  /**
   * Specify an optional className to be added to your Alert
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Alert for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Optional prop to show close icon for dismissal of the Alert
   */
  closeIcon: PropTypes.bool
}

Alert.defaultProps = {
  kind: 'success',
  role: 'alert',
  CustomIcon: null,
  isCloseable: true,
  closeButtonText: 'Close',
  closeButtonAriaLabel: 'close the alert',
  'data-test': 'component-alert',
  closeIcon: false,
  onClose: /* istanbul ignore next */ () => {}
}

export default Alert
